import { Image, Slider, Space, message } from "antd";
import { useEffect, useRef, useState } from "react";
import classes from "../Viewer.module.scss";
import {
  CloseCircleOutlined,
  RotateLeftOutlined,
  RotateRightOutlined,
  SwapOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  ScanOutlined,
  FileImageOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { useAppDispatch } from "store/hook";
import { resetViewerSlice } from "store/slices/viewerSlice";
import { useTranslation } from "react-i18next";
import { IOCRParagraph } from "interfaces";
import ocr from "api/ocr";
import { useSearchParams } from "react-router";
import OCRTextView from "./OCRTextView/OCRTextView";

const POLL_INTERVAL_MS = 5000;
const POLL_MAX_ATTEMPTS = 20;
const ZOOM_RATIO = 1.5;

const scaleToSlider = (scale: number): number =>
  Math.round(Math.log(scale) / Math.log(ZOOM_RATIO));

type ImageViewerProps = {
  url: any;
  onClose?: () => void;
  downloadDocument?: () => void;
  isStatic?: boolean;
};

const ImageViewer = ({ url, onClose, isStatic }: ImageViewerProps) => {
  const [visible, setVisible] = useState(onClose ? true : false);
  const [ocrParagraphs, setOcrParagraphs] = useState<IOCRParagraph[] | null>(null);
  const [isOCRProcessing, setIsOCRProcessing] = useState(false);
  const [showText, setShowText] = useState(false);
  const [imageNaturalWidth, setImageNaturalWidth] = useState<number | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const documentId = searchParams.get("document_id");

  useEffect(() => {
    return () => stopPolling();
  }, []);

  useEffect(() => {
    if (!url) return;
    const img = new window.Image();
    img.onload = () => setImageNaturalWidth(img.naturalWidth);
    img.src = url;
  }, [url]);

  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const pollStatus = (docId: string, attempt: number = 0) => {
    if (attempt >= POLL_MAX_ATTEMPTS) {
      setIsOCRProcessing(false);
      message.error(t("Documents.ocr.error"));
      return;
    }

    pollTimerRef.current = setTimeout(async () => {
      try {
        const { data } = await ocr.getRecognitionStatus(docId);

        if (data.status === "ready") {
          setOcrParagraphs(data.bbox_response ?? []);
          setShowText(true);
          setIsOCRProcessing(false);
        } else if (data.status === "in_progress") {
          pollStatus(docId, attempt + 1);
        } else {
          message.error(t("Documents.ocr.error"));
          setIsOCRProcessing(false);
        }
      } catch (error: any) {
        if (error?.response?.status === 502) {
          message.error(t("Documents.ocr.qualityError"));
        } else {
          message.error(t("Documents.ocr.error"));
        }
        setIsOCRProcessing(false);
      }
    }, POLL_INTERVAL_MS);
  };

  const handleRecognizeText = async () => {
    if (isOCRProcessing) return;
    if (!documentId) {
      message.error(t("Documents.ocr.error"));
      return;
    }

    setIsOCRProcessing(true);
    setOcrParagraphs(null);
    setShowText(false);

    try {
      const { data } = await ocr.recognizeImageText(documentId);

      if (data.status === "ready") {
        setOcrParagraphs(data.bbox_response ?? []);
        setShowText(true);
        setIsOCRProcessing(false);
      } else if (data.status === "in_progress") {
        pollStatus(documentId);
      } else {
        message.error(t("Documents.ocr.error"));
        setIsOCRProcessing(false);
      }
    } catch (error: any) {
      if (error?.response?.status === 502) {
        message.error(t("Documents.ocr.qualityError"));
      } else {
        message.error(t("Documents.ocr.error"));
      }
      setIsOCRProcessing(false);
    }
  };

  const handleClose = () => {
    stopPolling();
    setOcrParagraphs(null);
    setShowText(false);
    setIsOCRProcessing(false);
    onClose && onClose();
    onClose && dispatch(resetViewerSlice());
  };

  const handleSliderChange = (sliderValue: number, actions: any, currentScale: number) => {
    const diff = sliderValue - scaleToSlider(currentScale);
    if (diff > 0) {
      for (let i = 0; i < diff; i++) actions.onZoomIn();
    } else if (diff < 0) {
      for (let i = 0; i < Math.abs(diff); i++) actions.onZoomOut();
    }
  };

  return (
    <div className={isStatic ? classes.staticImageViewer : classes.imageViewerContainer}>
      <Image
        style={{ display: onClose || visible ? "none" : "flex" }}
        src={url}
        preview={{
          closeIcon: <CloseCircleOutlined style={{ fontSize: 32, color: "red" }} />,
          visible,
          src: url,
          onVisibleChange: (value) => {
            if (!value) handleClose();
            setVisible(value);
          },
          imageRender: (originalNode) =>
            showText && ocrParagraphs ? (
              <OCRTextView paragraphs={ocrParagraphs} imageWidth={imageNaturalWidth} />
            ) : (
              originalNode
            ),
          toolbarRender: (_, { transform, actions }) => {
            const sliderValue = scaleToSlider(transform.scale);

            return (
              <Space size="middle" className={classes.customToolbar}>
                {!showText && (
                  <>
                    <SwapOutlined
                      onClick={actions.onFlipY}
                      className={classes.toolbarIcon}
                      style={{ transform: "rotate(90deg)" }}
                      title={t("Documents.viewer.flipVertical")}
                    />
                    <SwapOutlined
                      onClick={actions.onFlipX}
                      className={classes.toolbarIcon}
                      title={t("Documents.viewer.flipHorizontal")}
                    />
                    <RotateLeftOutlined
                      onClick={actions.onRotateLeft}
                      className={classes.toolbarIcon}
                      title={t("Documents.viewer.rotateLeft")}
                    />
                    <RotateRightOutlined
                      onClick={actions.onRotateRight}
                      className={classes.toolbarIcon}
                      title={t("Documents.viewer.rotateRight")}
                    />
                    <Space size="small" align="center" className={classes.zoomControls}>
                      <ZoomOutOutlined
                        onClick={actions.onZoomOut}
                        className={classes.toolbarIcon}
                        title={t("Documents.viewer.zoomOut")}
                      />
                      <Slider
                        min={0}
                        max={10}
                        step={1}
                        value={sliderValue}
                        onChange={(value) => handleSliderChange(value, actions, transform.scale)}
                        className={classes.zoomSlider}
                        tooltip={{
                          formatter: (value) =>
                            `${Math.round(Math.pow(ZOOM_RATIO, value || 0) * 100)}%`,
                        }}
                      />
                      <ZoomInOutlined
                        onClick={actions.onZoomIn}
                        className={classes.toolbarIcon}
                        title={t("Documents.viewer.zoomIn")}
                      />
                    </Space>
                  </>
                )}

                <ScanOutlined
                  spin={isOCRProcessing}
                  onClick={handleRecognizeText}
                  className={`${classes.toolbarIcon} ${isOCRProcessing ? classes.toolbarIconDisabled : ""}`}
                  title={t("Documents.ocr.recognize")}
                />

                {ocrParagraphs && (
                  <span
                    onClick={() => setShowText((prev) => !prev)}
                    className={`${classes.toolbarIcon} ${showText ? classes.toolbarIconActive : ""}`}
                    title={showText ? t("Documents.ocr.showImage") : t("Documents.ocr.showText")}
                  >
                    {showText ? <FileImageOutlined /> : <FileTextOutlined />}
                  </span>
                )}
              </Space>
            );
          },
        }}
      />
    </div>
  );
};

export default ImageViewer;
